import { type Hex, Pix } from '../icons/pix';

/**
 * Drawing in plate units: a plate is authored once, in units (one unit is one pixel at 1×), and
 * drawn into a pixel buffer at `s` pixels per unit. Rects and sprites become s×s blocks; curves,
 * discs and polygons are rasterised at the buffer's own resolution, so a plate drawn at 2× gets
 * rounder edges than at 1× and stays hard-pixelled at both.
 */

export type Pt = readonly [number, number];

export class Pen {
  constructor(
    readonly pix: Pix,
    /** Pixels per unit. */
    readonly s: number,
  ) {}

  /** A blank pen of the same scale, the size of this one (a separate layer). */
  layer(): Pen {
    return new Pen(new Pix(this.pix.w, this.pix.h), this.s);
  }

  /** One unit (an s×s block). */
  px(x: number, y: number, c: Hex): this {
    const s = this.s;
    this.pix.rect(Math.round(x * s), Math.round(y * s), s, s, c);
    return this;
  }

  /** A rect of units. */
  rect(x: number, y: number, w: number, h: number, c: Hex): this {
    const s = this.s;
    const x0 = Math.round(x * s);
    const y0 = Math.round(y * s);
    this.pix.rect(x0, y0, Math.round((x + w) * s) - x0, Math.round((y + h) * s) - y0, c);
    return this;
  }

  /** Clears a rect of units. */
  clear(x: number, y: number, w: number, h: number): this {
    const s = this.s;
    for (let j = Math.round(y * s); j < Math.round((y + h) * s); j++)
      for (let i = Math.round(x * s); i < Math.round((x + w) * s); i++) this.pix.erase(i, j);
    return this;
  }

  /** A filled ellipse. */
  ell(cx: number, cy: number, rx: number, ry: number, c: Hex): this {
    const s = this.s;
    this.pix.ell(cx * s, cy * s, rx * s, ry * s, c);
    return this;
  }

  disc(cx: number, cy: number, r: number, c: Hex): this {
    return this.ell(cx, cy, r, r, c);
  }

  /** A filled polygon. */
  poly(pts: readonly Pt[], c: Hex): this {
    const s = this.s;
    this.pix.poly(
      pts.map(([x, y]) => [x * s, y * s] as const),
      c,
    );
    return this;
  }

  /**
   * A stroke along points, `w` units wide: one pixel at 1× is a crisp Bresenham line; wider strokes
   * are discs stamped along the run.
   */
  line(x0: number, y0: number, x1: number, y1: number, c: Hex, w = 1): this {
    return this.taper(
      [
        [x0, y0],
        [x1, y1],
      ],
      w,
      w,
      c,
    );
  }

  path(pts: readonly Pt[], c: Hex, w = 1): this {
    return this.taper(pts, w, w, c);
  }

  /** A stroke whose width runs from `w0` to `w1` units along the points (a brush, a tail, a stem). */
  taper(pts: readonly Pt[], w0: number, w1: number, c: Hex): this {
    const s = this.s;
    if (w0 * s <= 1 && w1 * s <= 1) {
      for (let i = 0; i + 1 < pts.length; i++) {
        const [a, b] = [pts[i]!, pts[i + 1]!];
        this.pix.line(
          Math.floor(a[0] * s),
          Math.floor(a[1] * s),
          Math.floor(b[0] * s),
          Math.floor(b[1] * s),
          c,
        );
      }
      return this;
    }
    let total = 0;
    const seg: number[] = [];
    for (let i = 0; i + 1 < pts.length; i++) {
      const d = Math.hypot(pts[i + 1]![0] - pts[i]![0], pts[i + 1]![1] - pts[i]![1]);
      seg.push(d);
      total += d;
    }
    let run = 0;
    for (let i = 0; i + 1 < pts.length; i++) {
      const [a, b] = [pts[i]!, pts[i + 1]!];
      const n = Math.max(1, Math.ceil(seg[i]! * s * 2));
      for (let k = 0; k <= n; k++) {
        const u = k / n;
        const along = total ? (run + seg[i]! * u) / total : 0;
        const r = ((w0 + (w1 - w0) * along) * s) / 2;
        const x = (a[0] + (b[0] - a[0]) * u) * s;
        const y = (a[1] + (b[1] - a[1]) * u) * s;
        if (r <= 0.5) this.pix.px(Math.floor(x), Math.floor(y), c);
        else this.pix.ell(x, y, r, r, c);
      }
      run += seg[i]!;
    }
    return this;
  }

  /** The rim of an ellipse from angle `a0` to `a1` (radians, 0 = right, clockwise on screen). */
  arc(cx: number, cy: number, rx: number, ry: number, c: Hex, a0 = 0, a1 = Math.PI * 2, w = 1): this {
    const n = Math.max(6, Math.ceil(Math.abs(a1 - a0) * Math.max(rx, ry) * 1.5));
    const pts: Pt[] = [];
    for (let i = 0; i <= n; i++) {
      const a = a0 + ((a1 - a0) * i) / n;
      pts.push([cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]);
    }
    return this.path(pts, c, w);
  }

  /**
   * Pixel art from rows of characters, each a colour in `cols` (anything else is left clear),
   * every character one unit.
   */
  sprite(
    x: number,
    y: number,
    rows: readonly string[],
    cols: Readonly<Record<string, Hex>>,
    flip = false,
  ): this {
    const w = Math.max(...rows.map((r) => r.length));
    rows.forEach((row, j) => {
      for (let i = 0; i < row.length; i++) {
        const c = cols[row[i]!];
        if (c) this.px(x + (flip ? w - 1 - i : i), y + j, c);
      }
    });
    return this;
  }

  /** Draws another layer over this one. */
  over(layer: Pen): this {
    this.pix.blit(layer.pix);
    return this;
  }
}

/** Points along a quadratic curve from `a` to `c`, bending toward `b`. */
export function bez(a: Pt, b: Pt, c: Pt, n = 10): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    out.push([
      u * u * a[0] + 2 * u * t * b[0] + t * t * c[0],
      u * u * a[1] + 2 * u * t * b[1] + t * t * c[1],
    ]);
  }
  return out;
}

/** Points along a smooth curve through every given point (Catmull-Rom). */
export function spline(pts: readonly Pt[], per = 6): Pt[] {
  if (pts.length < 3) return [...pts];
  const out: Pt[] = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    const p0 = pts[Math.max(0, i - 1)]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[Math.min(pts.length - 1, i + 2)]!;
    for (let k = 0; k < per; k++) {
      const t = k / per;
      const t2 = t * t;
      const t3 = t2 * t;
      const f = (a: number, b: number, c: number, d: number) =>
        0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      out.push([f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  out.push(pts[pts.length - 1]!);
  return out;
}
